import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IconChevronDown } from "../icons";
import AppCheckbox from "./AppCheckbox";
import AppInput from "./AppInput";

const MENU_OFFSET = 8;
const MENU_MAX_HEIGHT = 232;
const MENU_VIEWPORT_GUTTER = 16;
const TYPEAHEAD_RESET_MS = 600;

function normalizeFlatOption(option) {
  if (typeof option === "string" || typeof option === "number") {
    return {
      type: "option",
      value: option,
      label: String(option),
      displayLabel: String(option),
      disabled: false,
    };
  }

  return {
    type: "option",
    value: option?.value,
    label: option?.label ?? String(option?.value ?? ""),
    displayLabel: option?.displayLabel ?? option?.label ?? String(option?.value ?? ""),
    leadingVisual: option?.leadingVisual ?? null,
    disabled: Boolean(option?.disabled),
  };
}

function normalizeOptions(options) {
  return options.map((entry, index) => {
    if (entry && typeof entry === "object" && Array.isArray(entry.options)) {
      return {
        type: "group",
        key: entry.key ?? entry.label ?? `group-${index}`,
        label: entry.label ?? `Group ${index + 1}`,
        options: entry.options.map(normalizeFlatOption),
      };
    }

    return normalizeFlatOption(entry);
  });
}

function filterEntries(entries, query) {
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) {
    return entries;
  }

  return entries
    .map((entry) => {
      if (entry.type === "group") {
        const filteredOptions = entry.options.filter((option) =>
          option.label.toLowerCase().includes(normalizedQuery)
        );

        if (filteredOptions.length === 0) {
          return null;
        }

        return {
          ...entry,
          options: filteredOptions,
        };
      }

      return entry.label.toLowerCase().includes(normalizedQuery) ? entry : null;
    })
    .filter(Boolean);
}

export default function AppSelect({
  options = [],
  size = "md",
  variant = "default",
  invalid = false,
  className = "",
  value,
  defaultValue,
  onChange,
  placeholder = "Auswählen",
  disabled = false,
  readOnly = false,
  multiple = false,
  searchable = false,
  searchPlaceholder = "Option suchen",
  emptyMessage = "Keine Treffer gefunden",
  showSelectAll = false,
  selectAllLabel = "Alle auswählen",
  allowEmpty = false,
  emptyOptionLabel = "Keine Auswahl",
  leadingIcon: LeadingIcon,
  renderValue,
  // Optionale Aktion am Fuss des Menüs, z. B. "Standorte verwalten".
  // { label, icon, onClick } – ohne Angabe verhält sich das Menü wie bisher.
  footerAction = null,
  ...props
}) {
  const isControlled = value !== undefined;
  const fallbackDefaultValue = multiple ? [] : "";
  const initialValue = defaultValue !== undefined ? defaultValue : fallbackDefaultValue;
  const [internalValue, setInternalValue] = useState(initialValue);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [menuStyle, setMenuStyle] = useState(null);
  const [pendingFocusValue, setPendingFocusValue] = useState(null);
  const menuId = useId();
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const optionRefs = useRef(new Map());
  const typeaheadBufferRef = useRef("");
  const typeaheadTimeoutRef = useRef(null);

  const normalizedEntries = useMemo(() => normalizeOptions(options), [options]);
  const flatOptions = useMemo(
    () =>
      normalizedEntries.flatMap((entry) =>
        entry.type === "group" ? entry.options : [entry]
      ),
    [normalizedEntries]
  );
  const currentValue = isControlled ? value : internalValue;
  const selectedValues = useMemo(() => {
    if (!multiple) {
      return currentValue === undefined || currentValue === null || currentValue === ""
        ? []
        : [currentValue];
    }

    return Array.isArray(currentValue) ? currentValue : [];
  }, [currentValue, multiple]);
  const selectedOptions = useMemo(
    () => flatOptions.filter((option) => selectedValues.includes(option.value)),
    [flatOptions, selectedValues]
  );
  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);
  const selectedOption = !multiple ? selectedOptions[0] ?? null : null;
  const enabledFlatOptions = useMemo(
    () => flatOptions.filter((option) => !option.disabled),
    [flatOptions]
  );
  const filteredEntries = useMemo(
    () => filterEntries(normalizedEntries, searchable ? searchQuery : ""),
    [normalizedEntries, searchQuery, searchable]
  );
  const displayValue = useMemo(() => {
    if (!multiple) {
      return selectedOption ? selectedOption.displayLabel : placeholder;
    }

    if (selectedOptions.length === 0) {
      return placeholder;
    }

    if (selectedOptions.length <= 2) {
      return selectedOptions.map((option) => option.displayLabel).join(", ");
    }

    return `${selectedOptions
      .slice(0, 2)
      .map((option) => option.displayLabel)
      .join(", ")} +${selectedOptions.length - 2}`;
  }, [multiple, placeholder, selectedOption, selectedOptions]);
  const renderedDisplayValue = typeof renderValue === "function"
    ? renderValue(selectedValues, selectedOptions)
    : !multiple && selectedOption?.leadingVisual
      ? (
          <>
            <span className="ds-select__option-visual" aria-hidden="true">{selectedOption.leadingVisual}</span>
            <span className="ds-select__value-label">{displayValue}</span>
          </>
        )
      : displayValue;

  const classes = [
    "ds-select",
    variant === "brand" ? "ds-select--brand" : "",
    LeadingIcon ? "ds-select--with-leading-icon" : "",
    size === "sm" ? "ds-control--sm" : "",
    size === "md" ? "ds-control--md" : "",
    invalid ? "ds-control--invalid" : "",
    open ? "ds-select--open" : "",
    multiple ? "ds-select--multiple" : "",
    searchable ? "ds-select--searchable" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const isTopbarYearSelect = className.split(" ").includes("ds-topbar__year-select");
  const menuClasses = [
    "ds-select-menu",
    isTopbarYearSelect ? "ds-select-menu--topbar-year" : "",
  ]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setMenuStyle(null);
      return undefined;
    }

    function handlePointerDown(event) {
      const target = event.target;

      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }

      setOpen(false);
    }

    function handleEscape(event) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  useEffect(() => {
    if (disabled || readOnly) {
      setOpen(false);
    }
  }, [disabled, readOnly]);

  useEffect(() => {
    return () => {
      if (typeaheadTimeoutRef.current) {
        window.clearTimeout(typeaheadTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!pendingFocusValue) {
      return;
    }

    const optionNode = optionRefs.current.get(String(pendingFocusValue));

    if (!optionNode) {
      return;
    }

    optionNode.focus();
    optionNode.scrollIntoView({ block: "nearest" });
    setPendingFocusValue(null);
  }, [filteredEntries, open, pendingFocusValue]);

  useLayoutEffect(() => {
    if (!open || typeof window === "undefined") {
      return undefined;
    }

    function updateMenuPosition() {
      if (!rootRef.current || !menuRef.current) {
        return;
      }

      const triggerRect = rootRef.current.getBoundingClientRect();
      const menuRect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const availableBelow = Math.max(
        0,
        viewportHeight - triggerRect.bottom - MENU_OFFSET - MENU_VIEWPORT_GUTTER
      );
      const availableAbove = Math.max(
        0,
        triggerRect.top - MENU_OFFSET - MENU_VIEWPORT_GUTTER
      );
      const naturalHeight = menuRect.height || MENU_MAX_HEIGHT;
      const preferredHeight = Math.min(naturalHeight, MENU_MAX_HEIGHT);
      const openAbove = availableBelow < preferredHeight && availableAbove > availableBelow;
      const maxHeight = Math.min(
        MENU_MAX_HEIGHT,
        openAbove ? availableAbove : availableBelow
      );
      const renderedHeight = Math.min(preferredHeight, maxHeight || MENU_MAX_HEIGHT);
      const width = Math.min(
        triggerRect.width,
        viewportWidth - MENU_VIEWPORT_GUTTER * 2
      );
      const left = Math.min(
        Math.max(MENU_VIEWPORT_GUTTER, triggerRect.left),
        viewportWidth - MENU_VIEWPORT_GUTTER - width
      );
      const top = openAbove
        ? Math.max(
            MENU_VIEWPORT_GUTTER,
            triggerRect.top - MENU_OFFSET - renderedHeight
          )
        : triggerRect.bottom + MENU_OFFSET;

      setMenuStyle({
        top,
        left,
        width,
        maxHeight: maxHeight || MENU_MAX_HEIGHT,
        visibility: "visible",
      });
    }

    updateMenuPosition();

    const animationFrameId = window.requestAnimationFrame(updateMenuPosition);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => updateMenuPosition())
        : null;

    if (resizeObserver) {
      if (rootRef.current) {
        resizeObserver.observe(rootRef.current);
      }

      if (menuRef.current) {
        resizeObserver.observe(menuRef.current);
      }
    }

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
      resizeObserver?.disconnect();
    };
  }, [open, filteredEntries, searchQuery, searchable]);

  function createChangeEvent(nextValue, nextSelection) {
    const target = {
      value: nextValue,
      selection: nextSelection,
      multiple,
      name: props.name,
    };

    return {
      type: "change",
      target,
      currentTarget: target,
    };
  }

  function updateValue(nextValue, nextSelection) {
    if (!isControlled) {
      setInternalValue(nextValue);
    }

    if (onChange) {
      const event = createChangeEvent(nextValue, nextSelection);
      onChange(event);
    }
  }

  function handleSingleSelect(nextValue) {
    const nextOption = flatOptions.find((option) => option.value === nextValue) ?? null;
    updateValue(nextValue, nextOption);
    setOpen(false);
  }

  function handleClearSelection() {
    updateValue("", null);
    setOpen(false);
  }

  function handleMultiToggle(nextValue) {
    const alreadySelected = selectedSet.has(nextValue);
    const nextValues = alreadySelected
      ? selectedValues.filter((valueItem) => valueItem !== nextValue)
      : [...selectedValues, nextValue];
    const nextOptions = flatOptions.filter((option) => nextValues.includes(option.value));

    updateValue(nextValues, nextOptions);
  }

  function handleSelectAll() {
    if (disabled || readOnly || !multiple) {
      return;
    }

    const selectableValues = enabledFlatOptions.map((option) => option.value);
    const allSelected =
      selectableValues.length > 0 &&
      selectableValues.every((optionValue) => selectedSet.has(optionValue));
    const nextValues = allSelected
      ? selectedValues.filter((selectedValue) => !selectableValues.includes(selectedValue))
      : [...selectedValues, ...selectableValues.filter((optionValue) => !selectedSet.has(optionValue))];
    const nextOptions = flatOptions.filter((option) => nextValues.includes(option.value));

    updateValue(nextValues, nextOptions);
  }

  function handleSelect(nextValue) {
    if (disabled || readOnly) {
      return;
    }

    if (multiple) {
      handleMultiToggle(nextValue);
      return;
    }

    handleSingleSelect(nextValue);
  }

  function handleGroupToggle(group) {
    if (disabled || readOnly || !multiple) {
      return;
    }

    const groupValues = group.options
      .filter((option) => !option.disabled)
      .map((option) => option.value);
    const allSelected = groupValues.length > 0 && groupValues.every((groupValue) => selectedSet.has(groupValue));
    const nextValues = allSelected
      ? selectedValues.filter((selectedValue) => !groupValues.includes(selectedValue))
      : [...selectedValues, ...groupValues.filter((groupValue) => !selectedSet.has(groupValue))];
    const nextOptions = flatOptions.filter((option) => nextValues.includes(option.value));

    updateValue(nextValues, nextOptions);
  }

  function clearTypeaheadBuffer() {
    typeaheadBufferRef.current = "";

    if (typeaheadTimeoutRef.current) {
      window.clearTimeout(typeaheadTimeoutRef.current);
      typeaheadTimeoutRef.current = null;
    }
  }

  function scheduleTypeaheadBufferReset() {
    if (typeaheadTimeoutRef.current) {
      window.clearTimeout(typeaheadTimeoutRef.current);
    }

    typeaheadTimeoutRef.current = window.setTimeout(() => {
      typeaheadBufferRef.current = "";
      typeaheadTimeoutRef.current = null;
    }, TYPEAHEAD_RESET_MS);
  }

  function getTypeaheadStartIndex() {
    const activeValue = document.activeElement?.getAttribute?.("data-option-value");

    if (activeValue) {
      const activeIndex = enabledFlatOptions.findIndex((option) => String(option.value) === activeValue);

      if (activeIndex >= 0) {
        return activeIndex + 1;
      }
    }

    if (!multiple && selectedOption && !selectedOption.disabled) {
      const selectedIndex = enabledFlatOptions.findIndex((option) => option.value === selectedOption.value);

      if (selectedIndex >= 0) {
        return selectedIndex + 1;
      }
    }

    return 0;
  }

  function findTypeaheadMatch(query, fromIndex) {
    if (!query) {
      return null;
    }

    const normalizedQuery = query.toLowerCase();
    const orderedOptions = [
      ...enabledFlatOptions.slice(fromIndex),
      ...enabledFlatOptions.slice(0, fromIndex),
    ];

    return (
      orderedOptions.find((option) => option.label.toLowerCase().startsWith(normalizedQuery)) ??
      null
    );
  }

  function handleTypeahead(character) {
    if (disabled || readOnly || searchable) {
      return;
    }

    const nextBuffer = `${typeaheadBufferRef.current}${character.toLowerCase()}`;
    const repeatedCharacter = nextBuffer.length > 1 && nextBuffer.split("").every((entry) => entry === nextBuffer[0]);
    const searchTerm = repeatedCharacter ? character.toLowerCase() : nextBuffer;
    const startIndex = repeatedCharacter ? getTypeaheadStartIndex() : 0;
    const match = findTypeaheadMatch(searchTerm, startIndex);

    typeaheadBufferRef.current = nextBuffer;
    scheduleTypeaheadBufferReset();

    if (!match) {
      return;
    }

    if (!open) {
      setOpen(true);
    }

    setPendingFocusValue(match.value);
  }

  function handleTriggerKeyDown(event) {
    if (disabled || readOnly) {
      return;
    }

    if (event.key === "Escape") {
      clearTypeaheadBuffer();
      return;
    }

    if (event.key.length === 1 && !event.altKey && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      handleTypeahead(event.key);
    }
  }

  function handleMenuKeyDown(event) {
    if (event.target?.tagName === "INPUT") {
      return;
    }

    if (event.key === "Escape") {
      clearTypeaheadBuffer();
      return;
    }

    if (event.key.length === 1 && !event.altKey && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      handleTypeahead(event.key);
    }
  }

  function renderOption(option, groupLabel) {
    const isSelected = selectedSet.has(option.value);
    const optionClasses = [
      "ds-select-menu__option",
      isSelected ? "ds-select-menu__option--selected" : "",
      multiple ? "ds-select-menu__option--multiple" : "",
      option.disabled ? "ds-select-menu__option--disabled" : "",
    ]
      .filter(Boolean)
      .join(" ");

    const optionRef = (node) => {
      const refKey = String(option.value);

      if (node) {
        optionRefs.current.set(refKey, node);
      } else {
        optionRefs.current.delete(refKey);
      }
    };

    if (multiple) {
      return (
        <div
          key={`${groupLabel || "root"}-${String(option.value)}`}
          className={optionClasses}
          ref={(node) => optionRef(node?.querySelector("input") ?? null)}
          role="option"
          aria-selected={isSelected ? "true" : "false"}
        >
          <AppCheckbox
            label={option.label}
            labelVariant={isSelected ? "label-1" : "body-1"}
            checked={isSelected}
            disabled={option.disabled}
            data-option-value={String(option.value)}
            onChange={() => handleSelect(option.value)}
          />
        </div>
      );
    }

    return (
      <button
        key={`${groupLabel || "root"}-${String(option.value)}`}
        type="button"
        className={optionClasses}
        ref={optionRef}
        onClick={() => handleSelect(option.value)}
        data-option-value={String(option.value)}
        role="option"
        aria-selected={isSelected ? "true" : "false"}
        disabled={option.disabled}
      >
        {option.leadingVisual ? (
          <span className="ds-select__option-visual" aria-hidden="true">{option.leadingVisual}</span>
        ) : null}
        <span className="ds-select-menu__option-label">{option.label}</span>
      </button>
    );
  }

  function renderEmptyOption() {
    const optionClasses = [
      "ds-select-menu__option",
      selectedValues.length === 0 ? "ds-select-menu__option--selected" : "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <button
        key="empty-selection"
        type="button"
        className={optionClasses}
        onClick={handleClearSelection}
        role="option"
        aria-selected={selectedValues.length === 0 ? "true" : "false"}
      >
        <span className="ds-select-menu__option-label ds-select-menu__option-label--empty">{emptyOptionLabel}</span>
      </button>
    );
  }

  function renderGroupLabel(entry) {
    if (!multiple) {
      return <div className="ds-select-menu__group-label">{entry.label}</div>;
    }

    const groupValues = entry.options
      .filter((option) => !option.disabled)
      .map((option) => option.value);
    const selectedCount = groupValues.filter((groupValue) => selectedSet.has(groupValue)).length;
    const allSelected = groupValues.length > 0 && selectedCount === groupValues.length;
    const partiallySelected = selectedCount > 0 && selectedCount < groupValues.length;

    return (
      <div
        className={`ds-select-menu__group-action ${allSelected ? "ds-select-menu__group-action--selected" : ""}`}
      >
        <AppCheckbox
          label={entry.label}
          labelVariant={allSelected ? "label-1" : "body-1"}
          checked={allSelected}
          indeterminate={partiallySelected}
          onChange={() => handleGroupToggle(entry)}
        />
      </div>
    );
  }

  function renderSelectAllOption() {
    const selectableValues = enabledFlatOptions.map((option) => option.value);
    const selectedCount = selectableValues.filter((optionValue) => selectedSet.has(optionValue)).length;
    const allSelected = selectableValues.length > 0 && selectedCount === selectableValues.length;
    const partiallySelected = selectedCount > 0 && selectedCount < selectableValues.length;

    return (
      <div
        className={`ds-select-menu__option ds-select-menu__option--multiple ds-select-menu__select-all ${
          allSelected ? "ds-select-menu__option--selected" : ""
        }`}
        role="option"
        aria-selected={allSelected ? "true" : "false"}
      >
        <AppCheckbox
          label={selectAllLabel}
          labelVariant={allSelected ? "label-1" : "body-1"}
          checked={allSelected}
          indeterminate={partiallySelected}
          disabled={selectableValues.length === 0}
          onChange={handleSelectAll}
        />
      </div>
    );
  }

  const menu =
    open && !disabled && !readOnly ? (
      <div
        ref={menuRef}
        className={menuClasses}
        id={menuId}
        role="listbox"
        aria-multiselectable={multiple ? "true" : undefined}
        style={menuStyle ?? { visibility: "hidden" }}
        onKeyDown={handleMenuKeyDown}
      >
        {searchable ? (
          <div className="ds-select-menu__search" onClick={(event) => event.stopPropagation()}>
            <AppInput
              size="sm"
              type="search"
              clearable
              autoFocus
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(event) => setSearchQuery(String(event.target?.value ?? ""))}
            />
          </div>
        ) : null}

        {allowEmpty && !multiple ? renderEmptyOption() : null}
        {multiple && showSelectAll ? renderSelectAllOption() : null}

        {filteredEntries.length > 0 ? (
          filteredEntries.map((entry) => {
            if (entry.type === "group") {
              return (
                <div key={entry.key} className="ds-select-menu__group">
                  {renderGroupLabel(entry)}
                  {entry.options.map((option) => renderOption(option, entry.label))}
                </div>
              );
            }

            return renderOption(entry, null);
          })
        ) : (
          <div className="ds-select-menu__empty">{emptyMessage}</div>
        )}

        {footerAction ? (
          <button
            type="button"
            className="ds-select-menu__footer-action"
            onClick={() => {
              setOpen(false);
              footerAction.onClick?.();
            }}
          >
            {footerAction.icon ? (
              <span className="ds-select-menu__footer-action-icon" aria-hidden="true">
                {footerAction.icon}
              </span>
            ) : null}
            <span>{footerAction.label}</span>
          </button>
        ) : null}
      </div>
    ) : null;

  return (
    <>
      <div className="ds-select-wrap" ref={rootRef}>
        <button
          type="button"
          className={classes}
          aria-haspopup="listbox"
          aria-expanded={open ? "true" : "false"}
          aria-controls={open ? menuId : undefined}
          data-invalid={invalid ? "true" : undefined}
          data-readonly={readOnly ? "true" : undefined}
          disabled={disabled}
          onKeyDown={handleTriggerKeyDown}
          onClick={() => {
            if (!readOnly) {
              setOpen((current) => !current);
            }
          }}
          {...props}
        >
          {LeadingIcon ? (
            <span className="ds-select__leading-icon" aria-hidden="true">
              <LeadingIcon sx={{ fontSize: 16 }} />
            </span>
          ) : null}
          <span
            className={`ds-select__value ${
              selectedOptions.length > 0 ? "ds-select__value--selected" : "ds-select__value--placeholder"
            }`}
          >
            {renderedDisplayValue}
          </span>
          <IconChevronDown className="ds-select__icon" sx={{ fontSize: 18 }} />
        </button>
      </div>

      {menu && typeof document !== "undefined" ? createPortal(menu, document.body) : menu}
    </>
  );
}
